import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileManager } from '@cubone/react-file-manager';
import '@cubone/react-file-manager/dist/style.css';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { RuntimeConfig } from '@/lib/runtime-config';
import { formatBytes, formatDateTime, formatRelativeTime } from '@/lib/format';
import { strings } from '@/lib/strings';
import { AlertTriangle, ArrowLeft, Folder, Info, Loader2, Monitor, MoveRight, Plus, Trash2 } from 'lucide-react';
import {
  createRepo,
  deleteRepo,
  getDownloadUrl,
  getMutationStatus,
  getRepoDevices,
  getRepoEntries,
  getRepoStatus,
  listRepos,
  mutateRepoEntry,
  type RepoMutationResult,
  type RepoDevice,
  type RepoEntriesResponse,
  type RepoStatus,
  type RepoSummary,
  WebApiError
} from '@/lib/web-api';

interface RepoBrowserPageProps {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
}

type FileManagerEntry = {
  name: string;
  isDirectory: boolean;
  path: string;
  updatedAt?: string;
  size?: number;
};

const previewableExtensions = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'txt', 'md', 'mp4', 'mov', 'mp3', 'wav']);
const textPreviewExtensions = new Set(['txt', 'md', 'json', 'csv', 'log', 'yaml', 'yml', 'xml', 'ini', 'cfg']);

const FilePreview = ({
  file,
  runtimeConfig,
  tenantId
}: {
  file: FileManagerEntry;
  runtimeConfig: RuntimeConfig;
  tenantId: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textLoading, setTextLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const isTextPreview = textPreviewExtensions.has(extension);
  const canPreview = previewableExtensions.has(extension);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setUrl(null);
      setTextContent(null);
      setTextLoading(false);
      setError(null);
      const cleaned = file.path.replace(/^\/+/, '');
      const segments = cleaned.split('/').filter(Boolean);
      const repoId = segments[0] ?? '';
      const repoPath = segments.slice(1).join('/');
      if (!repoId || !repoPath) {
        setError('Unable to preview this file.');
        return;
      }
      try {
        const signedUrl = await getDownloadUrl(runtimeConfig, tenantId, repoId, repoPath);
        if (cancelled) {
          return;
        }
        setUrl(signedUrl);
        if (isTextPreview) {
          setTextLoading(true);
          try {
            const response = await fetch(signedUrl);
            if (!response.ok) {
              throw new Error(`Preview request failed (${response.status})`);
            }
            const content = await response.text();
            if (!cancelled) {
              setTextContent(content);
            }
          } catch (err) {
            if (!cancelled) {
              setError(err instanceof Error ? err.message : 'Unable to load preview.');
            }
          } finally {
            if (!cancelled) {
              setTextLoading(false);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load preview.');
        }
      }
    };

    if (!file.isDirectory) {
      void load();
    }

    return () => {
      cancelled = true;
    };
  }, [file, runtimeConfig, tenantId, isTextPreview]);

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
          {url ? (
            <Button variant="outline" size="sm" onClick={() => window.open(url, '_blank')}>
              Download
            </Button>
          ) : null}
        </div>
        <div className="flex flex-col items-center gap-3 rounded-md border border-border/60 p-6 text-center text-sm text-muted-foreground">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!url || textLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
        </div>
        <div className="flex items-center justify-center rounded-md border border-border/60 p-8 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading preview...
        </div>
      </div>
    );
  }

  const downloadButton = (
    <Button variant="outline" size="sm" onClick={() => window.open(url, '_blank')}>
      Download
    </Button>
  );

  if (isTextPreview) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
          {downloadButton}
        </div>
        <pre className="max-h-[65vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/30 p-4 text-xs">
          {textContent ?? ''}
        </pre>
      </div>
    );
  }

  if (!canPreview) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
          {downloadButton}
        </div>
        <div className="flex flex-col items-center gap-3 rounded-md border border-border/60 p-6 text-center text-sm text-muted-foreground">
          <p>Preview is not available for this file type.</p>
        </div>
      </div>
    );
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
          {downloadButton}
        </div>
        <img src={url} alt={file.name} className="max-h-[65vh] w-full rounded-md object-contain" />
      </div>
    );
  }

  if (['mp4', 'mov'].includes(extension)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
          {downloadButton}
        </div>
        <video src={url} className="max-h-[65vh] w-full rounded-md" controls />
      </div>
    );
  }

  if (['mp3', 'wav'].includes(extension)) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="truncate text-sm font-medium">{file.name}</p>
          {downloadButton}
        </div>
        <audio src={url} className="w-full" controls />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="truncate text-sm font-medium">{file.name}</p>
        {downloadButton}
      </div>
      <iframe
        src={url}
        title={file.name}
        className="h-[65vh] w-full rounded-md border border-border/60"
      />
    </div>
  );
};

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const encodeSegments = (segments: string[]) =>
  segments.map(segment => encodeURIComponent(segment)).join('/');

const mapStatusLabel = (status?: RepoStatus) => {
  const state = status?.state?.toUpperCase();
  switch (state) {
    case 'IDLE':
      return { label: strings.syncState.idle, variant: 'success' as const };
    case 'SYNCING':
      return { label: strings.syncState.syncing, variant: 'accent' as const };
    case 'ERROR':
      return { label: strings.syncState.error, variant: 'error' as const };
    case 'OFFLINE':
      return { label: strings.syncState.offline, variant: 'warning' as const };
    default:
      return { label: strings.syncState.unknown, variant: 'default' as const };
  }
};

const splitRepoPath = (path: string) => {
  const cleaned = path.replace(/^\/+/, '');
  if (!cleaned) {
    return { repoId: null, repoPath: '' };
  }
  const segments = cleaned.split('/').filter(Boolean);
  const repoId = segments[0] ?? null;
  const repoPath = segments.slice(1).join('/');
  return { repoId, repoPath };
};

export const RepoBrowserPage = ({ runtimeConfig, tenantId }: RepoBrowserPageProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const wildcard = params['*'] ?? '';

  const rawSegments = useMemo(
    () => wildcard.split('/').filter(Boolean).map(decodeSegment),
    [wildcard]
  );

  const repoId = rawSegments[0] ?? null;
  const repoSegments = useMemo(() => {
    if (!repoId) {
      return [] as string[];
    }
    const remaining = rawSegments.slice(1);
    if (remaining[0] === 'files') {
      return remaining.slice(1);
    }
    return remaining;
  }, [rawSegments, repoId]);

  const repoPath = repoSegments.join('/');
  const currentPath = repoId ? `/${[repoId, ...repoSegments].join('/')}` : '';

  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [entries, setEntries] = useState<RepoEntriesResponse | null>(null);
  const [status, setStatus] = useState<RepoStatus | null>(null);
  const [devices, setDevices] = useState<RepoDevice[]>([]);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [entriesError, setEntriesError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [mutationMessage, setMutationMessage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileManagerEntry[]>([]);
  const [activeSection, setActiveSection] = useState<'summary' | 'files' | 'devices'>('summary');
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [movePickerPath, setMovePickerPath] = useState('');
  const [movePickerEntries, setMovePickerEntries] = useState<RepoEntriesResponse | null>(null);
  const [movePickerLoading, setMovePickerLoading] = useState(false);
  const [movePickerError, setMovePickerError] = useState<string | null>(null);
  const [summaryEntries, setSummaryEntries] = useState<RepoEntriesResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [reposLoading, setReposLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileManagerEntry | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRepoName, setNewRepoName] = useState('');
  const [newRepoId, setNewRepoId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [repoActionError, setRepoActionError] = useState<string | null>(null);
  const [deletingRepoId, setDeletingRepoId] = useState<string | null>(null);

  const activeRepo = useMemo(
    () => repos.find(repo => repo.repo_id === repoId) ?? null,
    [repos, repoId]
  );

  const selectedRepoId = useMemo(() => {
    if (selectedFiles.length === 0) {
      return null;
    }
    const first = splitRepoPath(selectedFiles[0].path).repoId;
    if (!first) {
      return null;
    }
    const sameRepo = selectedFiles.every(file => splitRepoPath(file.path).repoId === first);
    return sameRepo ? first : null;
  }, [selectedFiles]);

  useEffect(() => {
    let cancelled = false;

    const loadRepos = async () => {
      setReposLoading(true);
      setRepoError(null);
      try {
        const list = await listRepos(runtimeConfig, tenantId);
        if (!cancelled) {
          setRepos(list);
        }
      } catch {
        if (!cancelled) {
          setRepoError('Unable to load repositories.');
        }
      } finally {
        if (!cancelled) {
          setReposLoading(false);
        }
      }
    };

    void loadRepos();
    return () => {
      cancelled = true;
    };
  }, [runtimeConfig, tenantId]);

  useEffect(() => {
    if (!repoId) {
      setEntries(null);
      setStatus(null);
      setDevices([]);
      setEntriesError(null);
      return;
    }

    if (repos.length > 0 && !activeRepo) {
      setEntries(null);
      setStatus(null);
      setDevices([]);
      setEntriesError('Repository not found.');
      return;
    }

    let cancelled = false;
    const loadEntries = async () => {
      setEntriesLoading(true);
      setEntriesError(null);
      try {
        const [entriesResponse, statusResponse, devicesResponse] = await Promise.all([
          getRepoEntries(runtimeConfig, tenantId, repoId, repoPath || undefined),
          getRepoStatus(runtimeConfig, tenantId, repoId),
          getRepoDevices(runtimeConfig, tenantId, repoId)
        ]);

        if (cancelled) {
          return;
        }

        setEntries(entriesResponse);
        setStatus(statusResponse);
        setDevices(devicesResponse);
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (err instanceof WebApiError) {
          setEntriesError(
            err.code === 'IndexNotReady'
              ? 'Snapshot index is still building. Try again in a moment.'
              : err.message
          );
        } else {
          setEntriesError('Unable to load repository entries.');
        }
      } finally {
        if (!cancelled) {
          setEntriesLoading(false);
        }
      }
    };

    void loadEntries();

    return () => {
      cancelled = true;
    };
  }, [runtimeConfig, tenantId, repoId, repoPath, repos.length, activeRepo]);

  useEffect(() => {
    if (repoId) {
      setActiveSection('summary');
    } else {
      setActiveSection('files');
    }
    setSelectedFiles([]);
  }, [repoId]);

  useEffect(() => {
    if (!copyMessage) {
      return;
    }
    const timeout = window.setTimeout(() => setCopyMessage(null), 1500);
    return () => window.clearTimeout(timeout);
  }, [copyMessage]);

  useEffect(() => {
    if (!moveDialogOpen || !selectedRepoId) {
      return;
    }
    setMovePickerLoading(true);
    setMovePickerError(null);
    void getRepoEntries(runtimeConfig, tenantId, selectedRepoId, movePickerPath || undefined)
      .then(response => setMovePickerEntries(response))
      .catch(() => setMovePickerError('Unable to load folders.'))
      .finally(() => setMovePickerLoading(false));
  }, [moveDialogOpen, movePickerPath, runtimeConfig, tenantId, selectedRepoId]);

  useEffect(() => {
    if (!repoId || activeSection !== 'summary') {
      return;
    }
    setSummaryLoading(true);
    setSummaryEntries(null);
    void getRepoEntries(runtimeConfig, tenantId, repoId)
      .then(response => setSummaryEntries(response))
      .catch(() => setSummaryEntries(null))
      .finally(() => setSummaryLoading(false));
  }, [activeSection, repoId, runtimeConfig, tenantId]);

  const fileManagerFiles = useMemo(() => {
    const map = new Map<string, FileManagerEntry>();
    const add = (entry: FileManagerEntry) => {
      if (!map.has(entry.path)) {
        map.set(entry.path, entry);
      }
    };

    repos.forEach(repo => {
      add({
        name: repo.repo_id,
        isDirectory: true,
        path: `/${repo.repo_id}`,
        updatedAt: repo.created_at
      });
    });

    if (repoId) {
      let pathAccumulator = '';
      [repoId, ...repoSegments].forEach(segment => {
        pathAccumulator = `${pathAccumulator}/${segment}`;
        add({
          name: segment,
          isDirectory: true,
          path: pathAccumulator
        });
      });

      (entries?.entries ?? []).forEach(entry => {
        const entryName = entry.path.split('/').filter(Boolean).pop() ?? entry.path;
        if (!entryName) {
          return;
        }
        const entryPath = `/${repoId}/${entry.path}`.replace(/\/+/, '/');
        const updatedAt = entry.mtime_unix_ms ? new Date(entry.mtime_unix_ms).toISOString() : undefined;
        add({
          name: entryName,
          isDirectory: entry.type === 'dir',
          path: entryPath,
          updatedAt,
          size: entry.size
        });
      });
    }

    return Array.from(map.values());
  }, [repos, entries, repoId, repoSegments]);

  const summaryStats = useMemo(() => {
    const files = summaryEntries?.entries?.filter(entry => entry.type === 'file') ?? [];
    const totalSize = files.reduce((acc, entry) => acc + (entry.size ?? 0), 0);
    return {
      fileCount: files.length,
      totalSize
    };
  }, [summaryEntries]);

  const movePickerFiles = useMemo(() => {
    if (!selectedRepoId) {
      return [];
    }
    const map = new Map<string, FileManagerEntry>();
    const add = (entry: FileManagerEntry) => {
      if (!map.has(entry.path)) {
        map.set(entry.path, entry);
      }
    };

    add({
      name: selectedRepoId,
      isDirectory: true,
      path: `/${selectedRepoId}`,
      updatedAt: activeRepo?.created_at
    });

    let pathAccumulator = '';
    movePickerPath.split('/').filter(Boolean).forEach(segment => {
      pathAccumulator = `${pathAccumulator}/${segment}`;
      add({
        name: segment,
        isDirectory: true,
        path: `/${selectedRepoId}${pathAccumulator}`
      });
    });

    (movePickerEntries?.entries ?? []).forEach(entry => {
      if (entry.type !== 'dir') {
        return;
      }
      const entryName = entry.path.split('/').filter(Boolean).pop() ?? entry.path;
      if (!entryName) {
        return;
      }
      const entryPath = `/${selectedRepoId}/${entry.path}`.replace(/\/+/, '/');
      add({
        name: entryName,
        isDirectory: true,
        path: entryPath
      });
    });

    return Array.from(map.values());
  }, [selectedRepoId, movePickerEntries, movePickerPath, activeRepo?.created_at]);

  const handleFolderChange = useCallback((path: string) => {
    const cleaned = path.replace(/^\/+/, '');
    if (!cleaned) {
      navigate('/repos');
      return;
    }
    navigate(`/repos/${encodeSegments(cleaned.split('/'))}`);
  }, [navigate]);

  const handleMovePickerFolderChange = useCallback((path: string) => {
    if (!selectedRepoId) {
      return;
    }
    const { repoPath } = splitRepoPath(path);
    setMovePickerPath(repoPath ?? '');
  }, [selectedRepoId]);

  const handleDownload = useCallback(async (files: FileManagerEntry[]) => {
    for (const file of files) {
      if (file.isDirectory) {
        continue;
      }
      const { repoId: targetRepo, repoPath: targetPath } = splitRepoPath(file.path);
      if (!targetRepo || !targetPath) {
        continue;
      }
      try {
        const url = await getDownloadUrl(runtimeConfig, tenantId, targetRepo, targetPath);
        window.location.assign(url);
      } catch (err) {
        if (err instanceof WebApiError) {
          setEntriesError(err.message);
        } else {
          setEntriesError('Unable to download file.');
        }
      }
    }
  }, [runtimeConfig, tenantId]);

  const handleCopy = useCallback(async (files: FileManagerEntry[]) => {
    const paths = files.map(file => file.path).join('\n');
    try {
      await navigator.clipboard.writeText(paths);
      setCopyMessage('Path copied to clipboard');
    } catch {
      setCopyMessage('Unable to copy path');
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setRepoError(null);
    setEntriesError(null);
    setRepoActionError(null);
    setReposLoading(true);
    void listRepos(runtimeConfig, tenantId)
      .then(list => setRepos(list))
      .catch(() => setRepoError('Unable to load repositories.'))
      .finally(() => setReposLoading(false));
    if (repoId) {
      setEntriesLoading(true);
      void Promise.all([
        getRepoEntries(runtimeConfig, tenantId, repoId, repoPath || undefined),
        getRepoStatus(runtimeConfig, tenantId, repoId),
        getRepoDevices(runtimeConfig, tenantId, repoId)
      ])
        .then(([entriesResponse, statusResponse, devicesResponse]) => {
          setEntries(entriesResponse);
          setStatus(statusResponse);
          setDevices(devicesResponse);
          setEntriesError(null);
        })
        .catch(err => {
          if (err instanceof WebApiError) {
            setEntriesError(err.message);
          } else {
            setEntriesError('Unable to load repository entries.');
          }
        })
        .finally(() => setEntriesLoading(false));
    }
  }, [runtimeConfig, tenantId, repoId, repoPath]);

  const handleCreateRepository = useCallback(async () => {
    const trimmedName = newRepoName.trim();
    const trimmedId = newRepoId.trim();
    if (!trimmedName) {
      setCreateError('Repository name is required.');
      return;
    }
    if (trimmedId.includes('/')) {
      setCreateError('Repository ID cannot contain "/".');
      return;
    }

    setCreateSaving(true);
    setCreateError(null);
    setRepoActionError(null);
    try {
      const created = await createRepo(runtimeConfig, tenantId, {
        repo_name: trimmedName,
        repo_id: trimmedId || undefined
      });
      setCreateDialogOpen(false);
      setNewRepoName('');
      setNewRepoId('');
      handleRefresh();
      navigate(`/repos/${encodeURIComponent(created.repo_id)}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unable to create repository.');
    } finally {
      setCreateSaving(false);
    }
  }, [newRepoName, newRepoId, runtimeConfig, tenantId, handleRefresh, navigate]);

  const handleDeleteRepository = useCallback(async (repo: RepoSummary) => {
    const confirmed = window.confirm(
      `Delete repository "${repo.repo_name}" (${repo.repo_id})?\n\nThis removes the repository and its assignments.`
    );
    if (!confirmed) {
      return;
    }
    setDeletingRepoId(repo.repo_id);
    setRepoActionError(null);
    try {
      await deleteRepo(runtimeConfig, tenantId, repo.repo_id);
      handleRefresh();
    } catch (err) {
      setRepoActionError(err instanceof Error ? err.message : 'Unable to delete repository.');
    } finally {
      setDeletingRepoId(null);
    }
  }, [runtimeConfig, tenantId, handleRefresh]);

  const pollMutationJob = useCallback(async (
    targetRepo: string,
    jobId: string
  ): Promise<RepoMutationResult> => {
    const maxAttempts = 60;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const status = await getMutationStatus(runtimeConfig, tenantId, targetRepo, jobId);
      if (status.status === 'SUCCEEDED' || status.status === 'FAILED') {
        return status;
      }
      setMutationMessage(status.status === 'RUNNING' ? 'Applying change...' : 'Queued for processing...');
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    throw new WebApiError(504, 'MutationTimeout', 'Mutation is taking longer than expected.');
  }, [runtimeConfig, tenantId]);

  const runMutation = useCallback(async (
    action: 'rename' | 'move' | 'delete' | 'mkdir',
    file: FileManagerEntry,
    destinationPath?: string
  ) => {
    const { repoId: targetRepo, repoPath: targetPath } = splitRepoPath(file.path);
    if (!targetRepo || !targetPath) {
      setEntriesError('Select a file or folder inside a repository.');
      return;
    }
    setMutationLoading(true);
    setEntriesError(null);
    setMutationMessage('Submitting change...');
    try {
      const response = await mutateRepoEntry(runtimeConfig, tenantId, targetRepo, action, targetPath, destinationPath);
      const status = response.status === 'SUCCEEDED' || response.status === 'FAILED'
        ? response
        : await pollMutationJob(targetRepo, response.job_id);
      if (status.status === 'FAILED') {
        throw new WebApiError(500, 'MutationFailed', status.error || 'Mutation failed.');
      }
      handleRefresh();
    } catch (err) {
      if (err instanceof WebApiError) {
        setEntriesError(err.message);
      } else {
        setEntriesError('Unable to update repository.');
      }
    } finally {
      setMutationLoading(false);
      setMutationMessage(null);
    }
  }, [runtimeConfig, tenantId, handleRefresh, pollMutationJob]);

  const handleRename = useCallback(async (file: FileManagerEntry, newName: string) => {
    const { repoPath } = splitRepoPath(file.path);
    if (!repoPath) {
      setEntriesError('Select a file or folder inside a repository.');
      return;
    }
    const parentParts = repoPath.split('/').filter(Boolean);
    parentParts.pop();
    const destinationPath = parentParts.length ? `${parentParts.join('/')}/${newName}` : newName;
    await runMutation('rename', file, destinationPath);
  }, [runMutation]);

  const handleDelete = useCallback(async (files: FileManagerEntry[]) => {
    for (const file of files) {
      // skip root repo folders
      const { repoPath } = splitRepoPath(file.path);
      if (!repoPath) {
        setEntriesError('Select a file or folder inside a repository.');
        continue;
      }
      await runMutation('delete', file);
    }
  }, [runMutation]);

  const handleCreateFolder = useCallback(async (
    name: string,
    parentFolder: FileManagerEntry
  ) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setEntriesError('Folder name is required.');
      return;
    }
    if (trimmedName.includes('/')) {
      setEntriesError('Folder name cannot contain "/".');
      return;
    }
    const parentPath = parentFolder.path.replace(/\/$/, '');
    const nextPath = parentPath ? `${parentPath}/${trimmedName}` : `/${trimmedName}`;
    await runMutation('mkdir', {
      name: trimmedName,
      isDirectory: true,
      path: nextPath
    });
  }, [runMutation]);

  const handlePaste = useCallback(async (
    files: FileManagerEntry[],
    destinationFolder: FileManagerEntry,
    operationType: 'copy' | 'move'
  ) => {
    if (operationType !== 'move') {
      setEntriesError('Copy is not supported. Use Move... or Cut + Paste.');
      return;
    }
    const { repoId: destRepo, repoPath: destPath } = splitRepoPath(destinationFolder.path);
    if (!destRepo) {
      setEntriesError('Select a destination folder inside a repository.');
      return;
    }
    for (const file of files) {
      const { repoId: sourceRepo, repoPath: sourcePath } = splitRepoPath(file.path);
      if (!sourceRepo || !sourcePath) {
        setEntriesError('Select a file or folder inside a repository.');
        continue;
      }
      if (sourceRepo !== destRepo) {
        setEntriesError('Move across repositories is not supported.');
        continue;
      }
      const destinationPath = destPath ? `${destPath}/${file.name}` : file.name;
      await runMutation('move', file, destinationPath);
    }
  }, [runMutation]);

  const handleOpenMoveDialog = useCallback(() => {
    if (!selectedRepoId) {
      setEntriesError('Select files or folders from a single repository.');
      return;
    }
    setMovePickerPath(repoPath ?? '');
    setMoveDialogOpen(true);
  }, [selectedRepoId, repoPath]);

  const handleConfirmMove = useCallback(async () => {
    if (!selectedRepoId) {
      setEntriesError('Select files or folders from a single repository.');
      return;
    }
    if (selectedFiles.length === 0) {
      setEntriesError('Select at least one file or folder to move.');
      return;
    }
    for (const file of selectedFiles) {
      const { repoId: sourceRepo, repoPath: sourcePath } = splitRepoPath(file.path);
      if (!sourceRepo || !sourcePath || sourceRepo !== selectedRepoId) {
        continue;
      }
      const destinationPath = movePickerPath ? `${movePickerPath}/${file.name}` : file.name;
      await runMutation('move', file, destinationPath);
    }
    setMoveDialogOpen(false);
    setSelectedFiles([]);
  }, [movePickerPath, runMutation, selectedFiles, selectedRepoId]);

  const handleFileOpen = useCallback((file: FileManagerEntry) => {
    if (file.isDirectory) {
      return;
    }
    setPreviewFile(file);
  }, []);

  const statusBadge = mapStatusLabel(status ?? undefined);
  const syncTime = status?.last_sync_unix_ms;
  const canMutateCurrentRepo = Boolean(repoId);
  const movePickerCurrentPath = selectedRepoId
    ? `/${[selectedRepoId, ...movePickerPath.split('/').filter(Boolean)].join('/')}`
    : '';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold">
            {repoId ? (activeRepo?.repo_name || repoId) : 'Repositories'}
          </h2>
          {repoId ? (
            <p className="text-sm text-muted-foreground">
              {strings.labels.lastSync}: {syncTime ? formatRelativeTime(syncTime) : strings.syncState.unknown}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Browse and manage repositories.</p>
          )}
        </div>
        {repoId ? (
          <div className="flex items-center gap-2">
            {entriesLoading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
          </div>
        ) : null}
      </div>

      {repoError ? (
        <Alert className="border-error/30 bg-error/10">
          <AlertTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-error" />
            Something went wrong
          </AlertTitle>
          <AlertDescription>{repoError}</AlertDescription>
        </Alert>
      ) : null}

      {entriesError ? (
        <Alert className="border-error/30 bg-error/10">
          <AlertTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-error" />
            Something went wrong
          </AlertTitle>
          <AlertDescription>{entriesError}</AlertDescription>
        </Alert>
      ) : null}

      {repoActionError ? (
        <Alert className="border-error/30 bg-error/10">
          <AlertTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-error" />
            Repository action failed
          </AlertTitle>
          <AlertDescription>{repoActionError}</AlertDescription>
        </Alert>
      ) : null}

      {copyMessage ? (
        <Alert className="border-border/60 bg-muted/40">
          <AlertTitle>Clipboard</AlertTitle>
          <AlertDescription>{copyMessage}</AlertDescription>
        </Alert>
      ) : null}

      {mutationMessage ? (
        <Alert className="border-border/60 bg-muted/40">
          <AlertTitle>Repository update</AlertTitle>
          <AlertDescription>{mutationMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-6 lg:flex-row">
        {repoId ? (
          <aside className="w-full space-y-4 rounded-lg border border-border/60 bg-card/60 p-4 lg:w-60">
            <nav className="space-y-1">
              <Button
                variant={activeSection === 'summary' ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-2"
                onClick={() => setActiveSection('summary')}
              >
                <Info className="h-4 w-4" />
                Summary
              </Button>
              <Button
                variant={activeSection === 'files' ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-2"
                onClick={() => setActiveSection('files')}
              >
                <Folder className="h-4 w-4" />
                Files
              </Button>
              <Button
                variant={activeSection === 'devices' ? 'secondary' : 'ghost'}
                className="w-full justify-start gap-2"
                onClick={() => setActiveSection('devices')}
              >
                <Monitor className="h-4 w-4" />
                Devices
              </Button>
            </nav>
            <Separator />
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => navigate(`/repos/${repoId}/recycle-bin`)}
              >
                <Trash2 className="h-4 w-4" />
                Recycle bin
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2"
                onClick={() => navigate('/repos')}
              >
                <ArrowLeft className="h-4 w-4" />
                All repositories
              </Button>
            </div>
          </aside>
        ) : null}

        <div className="flex-1 space-y-6">
          {!repoId ? (
            <Card>
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Repositories</CardTitle>
                  <CardDescription>Create repositories, open them, or delete them.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      setCreateError(null);
                      setCreateDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add repository
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleRefresh}>
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reposLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : repos.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                    No repositories yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>ID</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {repos.map(repo => (
                        <TableRow key={repo.repo_id}>
                          <TableCell className="font-medium">{repo.repo_name}</TableCell>
                          <TableCell>{repo.repo_id}</TableCell>
                          <TableCell>
                            {repo.created_at ? new Date(repo.created_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/repos/${encodeURIComponent(repo.repo_id)}`)}
                              >
                                Open
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={deletingRepoId === repo.repo_id}
                                onClick={() => void handleDeleteRepository(repo)}
                              >
                                {deletingRepoId === repo.repo_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : null}

          {(!repoId || activeSection === 'files') ? (
            <Card>
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <CardTitle>Files</CardTitle>
                {repoId ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={selectedFiles.length === 0}
                      onClick={handleOpenMoveDialog}
                    >
                      <MoveRight className="h-4 w-4" />
                      Move...
                    </Button>
                  </div>
                ) : null}
              </CardHeader>
              <CardContent>
                {reposLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <FileManager
                      files={fileManagerFiles}
                      isLoading={reposLoading || entriesLoading || mutationLoading}
                      initialPath={currentPath}
                      layout="list"
                      height="640px"
                      primaryColor="#242436"
                      collapsibleNav
                      enableFilePreview={false}
                      permissions={{
                        create: canMutateCurrentRepo,
                        upload: false,
                        move: canMutateCurrentRepo,
                        rename: canMutateCurrentRepo,
                        delete: canMutateCurrentRepo,
                        copy: false,
                        download: true
                      }}
                      onFolderChange={handleFolderChange}
                      onDownload={handleDownload}
                      onCopy={handleCopy}
                      onRename={handleRename}
                      onDelete={handleDelete}
                      onCreateFolder={handleCreateFolder}
                      onPaste={handlePaste}
                      onFileOpen={handleFileOpen}
                      onSelectionChange={setSelectedFiles}
                      onRefresh={handleRefresh}
                      onError={(error: { message?: string } | null) => {
                        setEntriesError(error?.message || 'File manager error.');
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          {repoId && activeSection === 'summary' ? (
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
                <CardDescription>Repository overview and sync health.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 rounded-lg border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Repository</p>
                    <p className="text-lg font-semibold">{activeRepo?.repo_name || repoId}</p>
                    <p className="text-sm text-muted-foreground">ID: {repoId}</p>
                  </div>
                  <div className="space-y-2 rounded-lg border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Sync status</p>
                    <p className="text-lg font-semibold">{statusBadge.label}</p>
                    <p className="text-sm text-muted-foreground">
                      Last sync {syncTime ? formatRelativeTime(syncTime) : strings.syncState.unknown}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {syncTime ? formatDateTime(syncTime) : '—'}
                    </p>
                  </div>
                  <div className="space-y-2 rounded-lg border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Root files</p>
                    {summaryLoading ? (
                      <Skeleton className="h-6 w-24" />
                    ) : (
                      <>
                        <p className="text-lg font-semibold">{summaryStats.fileCount}</p>
                        <p className="text-sm text-muted-foreground">{formatBytes(summaryStats.totalSize)}</p>
                      </>
                    )}
                  </div>
                  <div className="space-y-2 rounded-lg border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Devices</p>
                    <p className="text-lg font-semibold">{devices.length}</p>
                    <p className="text-sm text-muted-foreground">
                      {devices.length === 0 ? 'No telemetry reported' : 'Active devices reporting'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {repoId && activeSection === 'devices' ? (
            <Card>
              <CardHeader>
                <CardTitle>Devices</CardTitle>
              </CardHeader>
              <CardContent>
              {entriesLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : devices.length === 0 ? (
                <Alert className="border-border/60 bg-muted/40">
                  <AlertTitle>No device telemetry yet</AlertTitle>
                  <AlertDescription>Telemetry will appear once devices report status.</AlertDescription>
                </Alert>
              ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Device</TableHead>
                        <TableHead>Platform</TableHead>
                        <TableHead>Last seen</TableHead>
                        <TableHead>Last sync</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {devices.map(device => (
                        <TableRow key={device.device_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{device.device_name ?? device.device_id}</p>
                              {device.user_upn ? (
                                <p className="text-xs text-muted-foreground">{device.user_upn}</p>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>{device.platform ?? '—'}</TableCell>
                          <TableCell>{device.last_seen_unix_ms ? formatRelativeTime(device.last_seen_unix_ms) : '—'}</TableCell>
                          <TableCell>{device.last_sync_unix_ms ? formatRelativeTime(device.last_sync_unix_ms) : '—'}</TableCell>
                          <TableCell>{device.state ?? strings.syncState.unknown}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Move items</DialogTitle>
            <DialogDescription>Select a destination folder for the selected items.</DialogDescription>
          </DialogHeader>
          {movePickerError ? (
            <Alert className="border-error/30 bg-error/10">
              <AlertTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-error" />
                Unable to load folders
              </AlertTitle>
              <AlertDescription>{movePickerError}</AlertDescription>
            </Alert>
          ) : (
            <FileManager
              files={movePickerFiles}
              isLoading={movePickerLoading}
              initialPath={movePickerCurrentPath}
              layout="list"
              height="420px"
              primaryColor="#242436"
              collapsibleNav
              permissions={{
                create: false,
                upload: false,
                move: false,
                rename: false,
                delete: false,
                copy: false,
                download: false
              }}
              onFolderChange={handleMovePickerFolderChange}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleConfirmMove()}>
              Move here
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create repository</DialogTitle>
            <DialogDescription>Set a display name, and optionally provide a specific repository ID.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Repository name</p>
              <Input
                value={newRepoName}
                onChange={event => setNewRepoName(event.target.value)}
                placeholder="Finance data"
              />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Repository ID (optional)</p>
              <Input
                value={newRepoId}
                onChange={event => setNewRepoId(event.target.value)}
                placeholder="finance-data"
              />
            </div>
            {createError ? (
              <p className="text-sm text-error">{createError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
            <Button disabled={createSaving} onClick={() => void handleCreateRepository()}>
              {createSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create repository'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(previewFile)}
        onOpenChange={open => {
          if (!open) {
            setPreviewFile(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] w-[min(96vw,1100px)] max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>File preview</DialogTitle>
            <DialogDescription>Preview opens on double-click. Use Download to save the file locally.</DialogDescription>
          </DialogHeader>
          {previewFile ? (
            <FilePreview file={previewFile} runtimeConfig={runtimeConfig} tenantId={tenantId} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
